import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, type To } from 'react-router-dom';

import { Agreement, AgreementParagraph } from '@/components/ui/agreement';
import ExternalLink from '@/components/ui/external-link';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useProfileAgreement, useProfileAgreementMutation } from '@/hooks/queries/use-profile-agreement';
import { AgreementKey } from '@/services/api/profile/agreement';

const ServicesAgreementContent = ({ onAccepted }: { onAccepted: VoidFunction }) => {
  const { isPending, isSuccess, mutate } = useProfileAgreementMutation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isRead, setIsRead] = useState(false);

  const handleAccept = () => {
    mutate(
      { key: AgreementKey.SERVICES_AGREEMENT },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => onAccepted(),
      },
    );
  };

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('servicesAgreement.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('servicesAgreement.description')}</PageHeadingDescription>
      </PageHeading>
      <PageCard className="-md:flex-1 -md:flex -md:overflow-hidden" size="lg" bordered fixedFooter>
        <PageCardContent className="-md:flex-1 -md:overflow-hidden pt-8 space-y-6">
          <Agreement className="-md:flex-1" onScrollEnd={() => setIsRead(true)}>
            <AgreementParagraph>{t('servicesAgreement.text.intro')}</AgreementParagraph>
            <AgreementParagraph>{t('servicesAgreement.text.authorised.intro')}</AgreementParagraph>
            <ul className="pl-8">
              {(t('servicesAgreement.text.authorised.points', { returnObjects: true }) as string[]).map((text, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: index matches translation index
                <li className="list-disc -xs:text-xs text-sm" key={index}>
                  {text}
                </li>
              ))}
            </ul>
            <AgreementParagraph className="font-medium">{t('servicesAgreement.text.disclaimer.title')}</AgreementParagraph>
            <AgreementParagraph className="whitespace-pre-line">
              <Trans
                i18nKey="servicesAgreement.text.disclaimer.text"
                values={{
                  linkText: t('servicesAgreement.text.disclaimer.link'),
                }}
                components={[
                  <ExternalLink key="servicesAgreement.text.disclaimer.link" href={t('servicesAgreement.text.disclaimer.link')} className="underline hover:no-underline">
                    {t('servicesAgreement.text.disclaimer.link')}
                  </ExternalLink>,
                ]}
              />
            </AgreementParagraph>
          </Agreement>
          <p className="text-sm">{t('servicesAgreement.confirm')}</p>
        </PageCardContent>
        <PageCardFooter>
          <Button className="w-full relative" size="lg" type="submit" variant="primary" disabled={!isRead || isPending || isSuccess} onClick={handleAccept}>
            <span className={isPending ? 'invisible' : ''}>{t('servicesAgreement.button')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </PageCard>
    </>
  );
};
ServicesAgreementContent.displayName = 'ServicesAgreementContent';

const ServicesAgreement = ({ next }: { next: To }) => {
  const { isLoading, data: agreement, invalidate: invalidateAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);

  if (isLoading) return <LoadingCard />;

  if (agreement?.date) return <Navigate to={next} replace />;

  return <ServicesAgreementContent onAccepted={invalidateAgreement} />;
};
ServicesAgreement.displayName = 'ServicesAgreement';

export default ServicesAgreement;
